import React, { useState, useEffect, useCallback, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Stack,
  Group,
  Text,
  Button,
  Paper,
  Badge,
  Skeleton,
  Alert,
  SimpleGrid,
} from "@mantine/core";
import { IconCreditCard, IconPlus, IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { ClientPaymentMethod, isCardExpired } from "../../types/cardOnFile";
import AddCardModal from "../common/AddCardModal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import PaymentMethodCard from "./PaymentMethodCard";
import styles from "./PaymentMethodsSection.module.css";
import { BusinessInfo, SalonContextType } from "../../types/context";

interface PaymentMethodsSectionProps {
  clientId: string;
  clientName: string;
  readOnly?: boolean;
  /** When set, shows a prototype-style section title row (e.g. on the client detail payment tab). */
  sectionTitle?: string;
  sectionSubtitle?: string;
}

export default function PaymentMethodsSection({
  clientId,
  clientName,
  readOnly = false,
  sectionTitle,
  sectionSubtitle,
}: PaymentMethodsSectionProps) {
  const {
    getClientPaymentMethods,
    deleteClientPaymentMethod,
    setDefaultClientPaymentMethod,
    setClientPaymentMethodNickname,
  } = useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const salonId = (businessInfo as BusinessInfo | null)?.id ?? "";
  const stripeAccountId =
    (businessInfo as BusinessInfo | null)?.stripe_connect_account_id ?? "";
  const isStripeOnboarded = Boolean(
    (businessInfo as BusinessInfo | null)?.stripe_connect_account_is_onboarded,
  );
  const hasStripeConnect = Boolean(stripeAccountId.trim()) && isStripeOnboarded;

  const [paymentMethods, setPaymentMethods] = useState<ClientPaymentMethod[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(hasStripeConnect);
  const [error, setError] = useState<string | null>(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [deletingCard, setDeletingCard] = useState<ClientPaymentMethod | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [editingNicknamePmId, setEditingNicknamePmId] = useState<number | null>(
    null,
  );
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);

  const fetchPaymentMethods = useCallback(async () => {
    if (!clientId || !hasStripeConnect) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getClientPaymentMethods(clientId);
      setPaymentMethods(response.paymentMethods || []);
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setError("Failed to load payment methods");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, getClientPaymentMethods, hasStripeConnect]);

  useEffect(() => {
    if (!hasStripeConnect) {
      setIsLoading(false);
      setPaymentMethods([]);
      return;
    }
    fetchPaymentMethods();
  }, [fetchPaymentMethods, hasStripeConnect]);

  const handleDeleteCard = async () => {
    if (!deletingCard) return;

    setIsDeleting(true);
    try {
      await deleteClientPaymentMethod(clientId, deletingCard.id);
      setPaymentMethods((prev) =>
        prev.filter((pm) => pm.id !== deletingCard.id),
      );
      setDeletingCard(null);
    } catch (err) {
      console.error("Error deleting payment method:", err);
      const message =
        err instanceof Error
          ? err.message
          : (err as any)?.message || "Failed to delete card";
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetDefault = async (card: ClientPaymentMethod) => {
    if (!clientId || !card?.id) return;
    if (readOnly || card.isDefault) return;
    if (isCardExpired(card)) return;

    setIsSettingDefault(true);
    setError(null);
    try {
      await setDefaultClientPaymentMethod(clientId, card.id);
      setPaymentMethods((prev) =>
        prev.map((pm) => ({
          ...pm,
          isDefault: pm.id === card.id,
        })),
      );
    } catch (err) {
      console.error("Error setting default payment method:", err);
      const message =
        err instanceof Error
          ? err.message
          : (err as any)?.message || "Failed to set default card";
      setError(message);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleAddCardSuccess = () => {
    fetchPaymentMethods();
  };

  const startNicknameEdit = (card: ClientPaymentMethod) => {
    if (readOnly) return;
    setNicknameError(null);
    setEditingNicknamePmId(card.id);
    setNicknameDraft(card.nickname ?? "");
  };

  const cancelNicknameEdit = () => {
    setEditingNicknamePmId(null);
    setNicknameDraft("");
    setNicknameError(null);
  };

  const saveNickname = async (pmId: number) => {
    if (!clientId) return;
    if (readOnly) return;

    const value = nicknameDraft;
    if (value.length > 64) {
      setNicknameError("Nickname must be 64 characters or less");
      return;
    }

    setIsSavingNickname(true);
    setNicknameError(null);
    try {
      const resp = await setClientPaymentMethodNickname(clientId, pmId, value);
      setPaymentMethods((prev) =>
        prev.map((pm) =>
          pm.id === pmId ? { ...pm, nickname: resp.nickname } : pm,
        ),
      );
      cancelNicknameEdit();
    } catch (err) {
      console.error("Error saving nickname:", err);
      const message =
        err instanceof Error
          ? err.message
          : (err as any)?.message || "Failed to save nickname";
      setNicknameError(message);
    } finally {
      setIsSavingNickname(false);
    }
  };

  const renderHeadingRow = (actions?: ReactNode) => (
    <Group
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="md"
      className={sectionTitle ? styles.sectionHead : undefined}
    >
      {sectionTitle ? (
        <Stack gap={4}>
          <Text fw={600} size="lg" c="dark.8">
            {sectionTitle}
          </Text>
          {sectionSubtitle ? (
            <Text size="sm" c="dimmed">
              {sectionSubtitle}
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Group gap="xs">
          <IconCreditCard size={18} />
          <Text fw={500}>Cards on File</Text>
        </Group>
      )}
      {actions}
    </Group>
  );

  if (!hasStripeConnect) {
    return (
      <Stack gap="sm">
        {renderHeadingRow()}
        <Paper p="md" withBorder className={styles.emptyState}>
          <Stack align="center" gap="md">
            <Text size="sm" c="dimmed" ta="center">
              Finish setting up your Stripe Account to start adding cards to
              customers.
            </Text>
            <Button component={Link} to="/admin/settings/payments" color="teal">
              Payment settings
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack gap="xs">
        {sectionTitle ? (
          renderHeadingRow()
        ) : (
          <Group justify="space-between">
            <Text fw={500}>Payment Methods</Text>
          </Group>
        )}
        <Skeleton height={60} radius="md" />
        <Skeleton height={60} radius="md" />
      </Stack>
    );
  }

  const addCardButton = readOnly ? null : sectionTitle ? (
    <Button
      variant="filled"
      size="sm"
      color="teal"
      className={styles.addCardFilled}
      leftSection={<IconPlus size={16} />}
      onClick={() => setShowAddCardModal(true)}
    >
      Add Card
    </Button>
  ) : (
    <Button
      size="xs"
      variant="light"
      color="teal"
      leftSection={<IconPlus size={14} />}
      onClick={() => setShowAddCardModal(true)}
    >
      Add Card
    </Button>
  );

  return (
    <Stack gap="md">
      {sectionTitle ? (
        renderHeadingRow(addCardButton)
      ) : (
        <Group justify="space-between">
          <Group gap="xs">
            <IconCreditCard size={18} />
            <Text fw={500}>Cards on File</Text>
            {paymentMethods.length > 0 && (
              <Badge size="sm" variant="light" color="gray">
                {paymentMethods.length}
              </Badge>
            )}
          </Group>
          {addCardButton}
        </Group>
      )}

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {paymentMethods.length === 0 ? (
        <Paper p="md" withBorder className={styles.emptyState}>
          <Stack align="center" gap="xs">
            <IconCreditCard size={32} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed" ta="center">
              No cards on file
            </Text>
            <Button
              size="xs"
              variant="subtle"
              color="teal"
              onClick={() => setShowAddCardModal(true)}
            >
              Add a card
            </Button>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {paymentMethods.map((card) => (
            <PaymentMethodCard
              key={card.id}
              card={card}
              clientDisplayName={clientName}
              readOnly={readOnly}
              editingNicknamePmId={editingNicknamePmId}
              nicknameDraft={nicknameDraft}
              nicknameError={nicknameError}
              isSavingNickname={isSavingNickname}
              isDeleting={isDeleting}
              isSettingDefault={isSettingDefault}
              onStartNicknameEdit={startNicknameEdit}
              onSaveNickname={saveNickname}
              onCancelNicknameEdit={cancelNicknameEdit}
              onNicknameDraftChange={(value) => {
                setNicknameDraft(value);
                if (nicknameError) setNicknameError(null);
              }}
              onSetDefault={handleSetDefault}
              onDelete={setDeletingCard}
            />
          ))}
        </SimpleGrid>
      )}

      {!readOnly && (
        <AddCardModal
          opened={showAddCardModal}
          onClose={() => setShowAddCardModal(false)}
          clientId={clientId}
          salonId={salonId}
          stripeAccountId={stripeAccountId}
          clientName={clientName}
          onSuccess={handleAddCardSuccess}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deletingCard}
        onClose={() => setDeletingCard(null)}
        onConfirm={handleDeleteCard}
        isLoading={isDeleting}
        title="Remove Card"
        message={
          deletingCard
            ? `Are you sure you want to remove the ${deletingCard.cardBrand} card ending in ${deletingCard.cardLast4}?`
            : ""
        }
      />
    </Stack>
  );
}
